import { memo, CSSProperties } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { formatAuthorData, isAssetTypeEditorEnabled } from 'utility/functions';
import { RelatedAsset, RelatedContentItem } from 'utility/types/dataProvider';
import { useTranslation } from 'react-i18next';

const PREVIEW_IMG_STYLE: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center',
};

export type TitleCellProps = {
  relatedAsset?: RelatedAsset | RelatedContentItem;
};

/**
 * TitleCell - Displays asset title with preview image and author information
 *
 * Re-used in Media Gallery, Additional Content, and Collections.
 * Kept generic to avoid dependencies on specific context hooks.
 */
export const TitleCell = memo(({ relatedAsset }: TitleCellProps) => {
  const theme = useTheme();
  const { activeSite } = useCosmos();
  const { t } = useTranslation();

  if (!relatedAsset) {
    return null;
  }

  const titleText = relatedAsset?.title ?? '';
  const relatedAssetUuid = 'uuid' in relatedAsset ? relatedAsset?.uuid : relatedAsset?.id;
  let authorData = null;
  let previewUrl = null;
  let editHref = undefined;

  if ('uuid' in relatedAsset) {
    authorData = relatedAsset?.authors ?? [];
    previewUrl =
      relatedAsset?.previews?.thumbnail?.url ||
      relatedAsset?.previews?.size1?.url ||
      relatedAsset?.previews?.size2?.url ||
      relatedAsset?.previews?.display?.url ||
      null;
    editHref = `/${activeSite}/editorial/edit/${relatedAssetUuid}`;
  }

  const fullAuthorsString = authorData ? formatAuthorData(authorData) : '';
  const truncatedAuthorsString = authorData
    ? formatAuthorData(authorData, { truncate: true })
    : '';
  const tooltipTxt =
    authorData && authorData.length > 1 ? `Authors: ${fullAuthorsString}` : undefined;

  const assetType = relatedAsset?.type;
  const disableLinking = !isAssetTypeEditorEnabled(assetType);
  const isLinkable = Boolean(editHref) && !disableLinking;
  const tooltipTitle = isLinkable
    ? `${t('blox.actions.open_in_new_tab')} - ${titleText}`
    : undefined;

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        gap: 1,
      }}
    >
      {previewUrl && (
        <Box
          sx={{
            width: 70,
            height: 50,
            flexShrink: 0,
            overflow: 'hidden',
            borderRadius: '4px',
          }}
        >
          {isLinkable ? (
            <a
              href={editHref}
              target="_blank"
              rel="noopener noreferrer"
              title={tooltipTitle}
            >
              <img
                alt="Preview"
                src={previewUrl}
                draggable={false}
                style={PREVIEW_IMG_STYLE}
              />
            </a>
          ) : (
            <div>
              <img
                alt="Preview"
                src={previewUrl}
                draggable={false}
                style={PREVIEW_IMG_STYLE}
              />
            </div>
          )}
        </Box>
      )}
      <Stack direction="column">
        <Typography
          component={isLinkable ? 'a' : 'span'}
          rel={isLinkable ? 'noopener noreferrer' : undefined}
          href={isLinkable ? editHref : undefined}
          target={isLinkable ? '_blank' : undefined}
          title={tooltipTitle}
          sx={{
            fontSize: 14,
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            fontWeight: theme.typography.fontWeightMedium,
            width: '100%',
            lineHeight: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            color: theme.palette.text.primary,
            textDecoration: 'none',
          }}
        >
          {titleText}
        </Typography>

        <Typography
          sx={{
            fontSize: 12,
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            fontWeight: theme.typography.fontWeightRegular,
            width: '100%',
            lineHeight: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          <Tooltip
            disableInteractive
            title={tooltipTxt}
          >
            <span>{truncatedAuthorsString}</span>
          </Tooltip>
        </Typography>
      </Stack>
    </Stack>
  );
});
TitleCell.displayName = 'TitleCell';
